import type { ReactNode } from 'react';
import styled from 'styled-components';
import type { PtoVolumePosition } from '@shared/api';
import { rawHint, sourceLabel } from './format';
import { normalizedHint, type NormalizedField } from './normalized';
import {
  isAiPosition,
  mismatchHint,
  partSourceHint,
  sourcePlace,
  splitSectionLabel,
  volumeSheet,
} from './rowRef';
import { SheetLink } from './SelectionCells';
import { Badge } from './styles';

const Hint = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  margin-top: 2px;
`;

/** Код части РД в «Разделе»: «СП01», «ВР01», «ЧТЖ05». */
const PartCode = styled.span`
  display: inline-block;
  margin-right: 6px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.xs};
`;

/** «⚠» в текстовом начертании (U+FE0E): цвет задаёт CSS, а не цветной эмодзи шрифта. */
const WARN_SIGN = '\u26A0\uFE0E';

/** Значок расхождения шифра листа с шифром комплекта; текст — в подсказке.
 *  `font-feature-settings: normal`: со стилистическими наборами GlobalStyle запасной
 *  шрифт Windows (Segoe UI Symbol) рисует «⚠» залитым прямоугольником. */
const Mismatch = styled.span`
  margin-left: 6px;
  font-weight: 700;
  font-feature-settings: normal;
  color: ${({ theme }) => theme.colors.status.warning};
  cursor: help;
`;

/** Место в РД; `source_ref` — в подсказке. */
const Place = styled.span`
  cursor: help;
`;

interface CellProps {
  position: PtoVolumePosition;
}

/** «Наименование»: имя позиции и пояснение к числу из `raw` (кратность, «вне суммы»,
 *  замечания входного контроля). `badge` — значок сразу после имени (у строк ВОР —
 *  «⚠ N» анализа комплектности). */
export function NameCell({ position: p, badge }: CellProps & { badge?: ReactNode }) {
  const hint = rawHint(p.raw);
  return (
    <>
      {p.name}
      {badge}
      <NormMark position={p} field="name" />
      {hint && <Hint>{hint}</Hint>}
    </>
  );
}

/** Пометка у ячейки, изменённой нормализацией на выходе (`raw.normalized`: число
 *  из ячейки, наименование по тексту листа): что взято и откуда, полное
 *  пояснение — в подсказке. В базе строка прежняя. */
export function NormMark({ position: p, field }: CellProps & { field: NormalizedField }) {
  const h = normalizedHint(p.raw, field);
  return h ? <Hint title={h.title}>{h.text}</Hint> : null;
}

/** «Раздел»: часть РД с основной надписи листа (`raw.sheet.label`) — бейдж кода и
 *  имя, при расхождении шифра листа с шифром комплекта — значок с подсказкой. У
 *  позиции ИИ ниже — прежний `section` (узел или система). Основной надписи у
 *  позиции ещё нет — прежний `section`. */
export function SectionCell({ position: p }: CellProps) {
  const sheet = volumeSheet(p.raw);
  if (!sheet?.label) return <>{p.section ?? '—'}</>;
  const { code, name } = splitSectionLabel(sheet.label);
  const node = isAiPosition(p) ? p.section?.trim() : null;
  const mismatch = sheet.mismatch ? mismatchHint(sheet.mismatch) : null;
  return (
    <>
      {code && <PartCode title={partSourceHint(sheet.source)}>{code}</PartCode>}
      {name}
      {mismatch && (
        <Mismatch role="img" aria-label={mismatch} title={mismatch}>
          {WARN_SIGN}
        </Mismatch>
      )}
      {node && <Hint>узел/система: {node}</Hint>}
    </>
  );
}

/** «Источник»: как извлечена позиция (таблица / текст / геометрия) и место в РД —
 *  «СП01, л. 2 (стр. PDF 56)», ссылка на место разбора (`source_ref`) — в
 *  подсказке. Основной надписи у позиции ещё нет — как раньше: ссылка и страница.
 *  Клик по месту открывает лист в окне исходного листа РД. */
export function SourceCell({
  position: p,
  onOpenSheet,
}: CellProps & { onOpenSheet: (p: PtoVolumePosition) => void }) {
  const open = p.page_number > 0 ? () => onOpenSheet(p) : null;
  if (!volumeSheet(p.raw)) {
    return (
      <>
        <Badge $tone="muted">{sourceLabel(p.source_role)}</Badge>{' '}
        {open ? (
          <SheetLink type="button" title="Открыть лист РД со строкой" onClick={open}>
            {p.source_ref}
            {p.page_number ? ` · стр. ${p.page_number}` : ''}
          </SheetLink>
        ) : (
          p.source_ref
        )}
      </>
    );
  }
  const place = sourcePlace(p) || '—';
  return (
    <>
      <Badge $tone="muted">{sourceLabel(p.source_role)}</Badge>{' '}
      {open ? (
        <SheetLink
          type="button"
          title={`${p.source_ref}\nОткрыть лист РД со строкой`}
          onClick={open}
        >
          {place}
        </SheetLink>
      ) : (
        <Place title={p.source_ref}>{place}</Place>
      )}
    </>
  );
}
